import { useMemo } from "react";

import {
  useRoleContext,
  useSentFollowRequests,
  useShipperNetworkGroups,
  useSwrFetch,
} from "hooks";

import { ConsolidatedShipmentVisibility } from "./ConsolidatedShipmentVisibility";
import { extractPendingExternalInviteEmails } from "./utils";

import type { UnifiedPartnerSelectorRef } from "./components/UnifiedPartnerSelector";

interface NetworkMember {
  email: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
}

interface ConsolidatedShipmentVisibilityContainerProps {
  // Form state - same pattern as ShipmentNotificationsSelectMenu
  notificationSettingsType: ShipmentNotificationType;
  selectedCarriers: string[];
  partnerGroups: string[];
  networkInviteEmails: string[];

  // State update handlers - same pattern as ShipmentNotificationsSelectMenu
  setShipmentNotificationType: (type: ShipmentNotificationType) => void;
  setShipmentNotificationEmails: (emails: string[]) => void;
  setPartnerGroups: (groups: string[]) => void;
  setNetworkInviteEmails: (emails: string[]) => void;

  // Props
  isProject?: boolean;
  onEmailInputRef?: (ref: UnifiedPartnerSelectorRef) => void;
}

export function ConsolidatedShipmentVisibilityContainer(
  props: ConsolidatedShipmentVisibilityContainerProps,
) {
  const {
    notificationSettingsType,
    selectedCarriers,
    partnerGroups,
    networkInviteEmails,
    setShipmentNotificationType,
    setShipmentNotificationEmails,
    setPartnerGroups,
    setNetworkInviteEmails,
    isProject = false,
    onEmailInputRef,
  } = props;

  // State for groups using existing hook
  const {
    networkGroups: availableGroups,
    networkGroupsError: groupsError,
    loadingNetworkGroups: loadingGroups,
  } = useShipperNetworkGroups();

  const { role } = useRoleContext();

  // Fetch confirmed partners (following carriers)
  const {
    data: followers,
    loading: loadingPartners,
    error: partnersError,
  } = useSwrFetch<FollowingCarrier[]>({
    url: role?.type === "SHIPPER" ? `/shippers/${role.id}/followers` : null,
    errorMessage: "Failed to fetch carrier network.",
  });

  // Invites this shipper already sent that are still awaiting a response. Only
  // external ones: an internal invite already resolves to a carrier role and is
  // outside this iteration's scope.
  const { sentFollowRequests, loading: loadingPendingInvites } =
    useSentFollowRequests();

  const pendingInviteEmails = useMemo<string[]>(
    () => extractPendingExternalInviteEmails(sentFollowRequests),
    [sentFollowRequests],
  );

  const confirmedPartners = useMemo<NetworkMember[]>(
    () =>
      (followers ?? []).map((carrier) => ({
        email: carrier.user.email,
        firstName: carrier.user.first_name,
        lastName: carrier.user.last_name,
        companyName: carrier.company?.name,
      })),
    [followers],
  );

  // Mapping handlers - same pattern as ShipmentNotificationsSelectMenu
  const onShipmentVisibilityChanged = (value: "PUBLIC" | "PRIVATE") => {
    if (value === "PUBLIC") {
      // Clear partner groups since they're not allowed for public shipments
      setPartnerGroups([]);

      if (notificationSettingsType === "PRIVATE_SHIPMENT") {
        setShipmentNotificationType("SELECTED_NETWORK_CARRIERS");
      }
      if (notificationSettingsType === "PRIVATE_SHIPMENT_ALL_NETWORK") {
        setShipmentNotificationType("ALL_CARRIER_NETWORK");
      }
    }

    if (value === "PRIVATE") {
      if (notificationSettingsType === "ALL_CARRIER_NETWORK") {
        setShipmentNotificationType("PRIVATE_SHIPMENT_ALL_NETWORK");
      }
      if (notificationSettingsType === "SELECTED_NETWORK_CARRIERS") {
        setShipmentNotificationType("PRIVATE_SHIPMENT");
      }
    }
  };

  const onAllPartnersSelectedChanged = (isSelected: boolean) => {
    if (isSelected) {
      if (notificationSettingsType === "SELECTED_NETWORK_CARRIERS") {
        setShipmentNotificationType("ALL_CARRIER_NETWORK");
      }
      if (notificationSettingsType === "PRIVATE_SHIPMENT") {
        setShipmentNotificationType("PRIVATE_SHIPMENT_ALL_NETWORK");
      }
    } else {
      if (notificationSettingsType === "ALL_CARRIER_NETWORK") {
        setShipmentNotificationType("SELECTED_NETWORK_CARRIERS");
      }
      if (notificationSettingsType === "PRIVATE_SHIPMENT_ALL_NETWORK") {
        setShipmentNotificationType("PRIVATE_SHIPMENT");
      }
    }
  };

  return (
    <ConsolidatedShipmentVisibility
      // Derived props - same pattern as ShipmentNotificationsSelectMenu
      shipmentVisibility={
        notificationSettingsType === "ALL_CARRIER_NETWORK" ||
        notificationSettingsType === "SELECTED_NETWORK_CARRIERS"
          ? "PUBLIC"
          : "PRIVATE"
      }
      selectedConfirmedPartnerEmails={selectedCarriers}
      selectedGroups={partnerGroups}
      selectedNetworkInviteEmails={networkInviteEmails}
      isAllPartnersSelected={
        notificationSettingsType === "ALL_CARRIER_NETWORK" ||
        notificationSettingsType === "PRIVATE_SHIPMENT_ALL_NETWORK"
      }
      // Mapped handlers
      onShipmentVisibilityChange={onShipmentVisibilityChanged}
      onConfirmedPartnerEmailsChange={setShipmentNotificationEmails}
      onSelectedGroupsChange={setPartnerGroups}
      onNetworkInviteEmailsChange={setNetworkInviteEmails}
      onAllPartnersSelectedChange={onAllPartnersSelectedChanged}
      // Pass-through props
      confirmedPartners={confirmedPartners}
      availableGroups={availableGroups}
      pendingInviteEmails={pendingInviteEmails}
      loadingPartners={loadingPartners}
      loadingGroups={loadingGroups}
      loadingPendingInvites={loadingPendingInvites}
      partnersError={partnersError}
      groupsError={groupsError}
      isProject={isProject}
      onEmailInputRef={onEmailInputRef}
    />
  );
}
